// datadog.js
'use strict';
import loadScriptTag from '../resource/loadScriptTag.js';
import { getIsInNode } from '../resource/getJsEnvironment.js';
import env from '../resource/env.js';
import { datadogLog } from '../resource/debug.js';

const isProd = 'production' === env.NODE_ENV;
const tagName = env.TAG_NAME;
const branchName = env.BRANCH_NAME;
const version = tagName || branchName || 'local';
const isOnTagVersion = !/-/.test(tagName);

const DATADOG_SCRIPT_URL =
  'https://www.datadoghq-browser-agent.com/us5/v5/datadog-rum.js';

const isServer = getIsInNode();

let datadogRum;

export const initDatadog = async ({ service, ...otherConfig } = {}) => {
  const clientToken = env.DATADOG_RUM_CLIENT_TOKEN;
  const applicationId = env.DATADOG_RUM_APPLICATION_ID;
  if (isServer || !clientToken || !applicationId || datadogRum) {
    datadogLog('bypass datadog init', {
      isServer,
      clientToken,
      applicationId,
      datadogRum,
    });
    return;
  }

  datadogLog('load datadog script');
  await loadScriptTag({
    id: 'datadog-script',
    async: false,
    src: DATADOG_SCRIPT_URL,
  });
  datadogLog('load datadog script done');

  datadogLog('wait for datadog ready');
  await new Promise(resolve => window.DD_RUM.onReady(resolve));
  datadogLog('datadog ready');
  datadogRum = window.DD_RUM;

  const allowedTracingUrls = env.DATADOG_ALLOWED_TRACING_URLS?.split(' ') || [];

  const sessionSampleRate = env.DATADOG_RUM_SAMPLE_RATE
    ? +env.DATADOG_RUM_SAMPLE_RATE
    : 0;
  const sessionReplaySampleRate = env.DATADOG_RUM_REPLAY_SAMPLE_RATE
    ? +env.DATADOG_RUM_REPLAY_SAMPLE_RATE
    : 0;

  const datadogConfig = {
    defaultPrivacyLevel: isProd ? 'mask-user-input' : 'allow',
    env: isProd ? (isOnTagVersion ? env.NODE_ENV : branchName) : 'local',
    service,
    version,
    clientToken,
    applicationId,
    allowedTracingUrls,
    sessionSampleRate,
    sessionReplaySampleRate,
    site: env.DATADOG_RUM_SITE,
    trackResources: env.DATADOG_RUM_TRACE_RESOURCE === '1',
    trackLongTasks: env.DATADOG_RUM_TRACE_LONG_TASKS === '1',
    trackUserInteractions: env.DATADOG_RUM_TRACE_USER_INTERACTIONS === '1',
    ...otherConfig,
  };

  datadogLog('config', datadogConfig);

  datadogLog('datadog init');
  window.DD_RUM.init(datadogConfig);
  datadogLog('datadog init done');

  return datadogRum;
};

export const setDatadogUserTag = (key, value) => {
  return datadogRum?.setUserProperty(key, value);
};

export const clearDatadogUser = () => {
  return datadogRum?.clearUser();
};

export const getDatadogRum = () => datadogRum;
